<template>
  <div id="judging-criteria-questions-list" class="list-container">
    <!-- Overlay Loader -->
    <v-overlay :value="OverlayLoadingFlag" z-index="9999">
      <v-progress-circular indeterminate size="64" color="#8950fc"></v-progress-circular>
    </v-overlay>

    <v-card class="list-card elevation-24">
      <!-- Professional Header -->
      <div class="list-header">
        <div class="header-left">
          <v-icon size="36" color="white" class="header-icon-badge"
            >mdi-file-document</v-icon
          >
          <div class="header-text-section">
            <h2 class="header-title">{{ PageTitle }}</h2>
            <p class="header-subtitle">{{ PageDescription }}</p>
          </div>
        </div>
        <div class="header-right">
          <v-btn
            icon
            dark
            @click="refreshPageData"
            class="header-action-btn"
            v-tooltip.left="'Refresh'"
          >
            <v-icon size="24">mdi-refresh</v-icon>
          </v-btn>
          <v-btn
            icon
            dark
            @click="closeQuestionsPopup"
            class="header-action-btn"
            v-tooltip.left="'Close'"
          >
            <v-icon size="24">mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Content Section -->
      <v-sheet class="list-content-sheet overflow-y-auto" max-height="600">
        <v-card-text class="list-body-content pa-6">
          <!-- Add Button Section -->
          <div class="add-button-section mb-6">
            <v-btn
              v-if="tableOptions1.CreateButtonFlag"
              @click="addRecordPrompt = true"
              color="#8950fc"
              elevation="6"
              rounded
              height="45"
              class="add-question-btn white--text"
            >
              <v-icon size="22" class="mr-2">mdi-plus-circle</v-icon>
              ADD NEW QUESTION
            </v-btn>
          </div>

          <!-- Empty State -->
          <v-card
            v-if="tableData1.length === 0"
            class="empty-state-card"
            elevation="0"
          >
            <v-card-text class="text-center pa-10">
              <v-icon size="80" color="#ccc" class="mb-4"
                >mdi-file-document-outline</v-icon
              >
              <h3 class="text-h6 text-grey-1 mb-2">No Questions Found</h3>
              <p class="text-grey-4 mb-4">
                No questions have been added for this criteria yet.
              </p>
              <v-btn
                @click="addRecordPrompt = true"
                color="#8950fc"
                outlined
                rounded
                class="mt-4"
              >
                <v-icon small left>mdi-plus</v-icon>
                Add Question
              </v-btn>
            </v-card-text>
          </v-card>

          <!-- Data Table Section -->
          <v-card
            v-if="tableData1.length > 0"
            class="table-wrapper-card elevation-8"
          >
            <!-- Search Bar -->
            <v-card-text class="pa-4 bg-light">
              <v-text-field
                v-model="search"
                placeholder="Search questions..."
                prepend-icon="mdi-magnify"
                outlined
                dense
                rounded
                class="search-field"
                style="max-width: 400px"
              ></v-text-field>
            </v-card-text>

            <!-- Table -->
            <v-data-table
              class="modern-questions-table"
              v-model="selected1"
              :search="search"
              :item-key="tableOptions1.ItemKey"
              :single-select="tableOptions1.SingleSelectFlag"
              :show-select="tableOptions1.ShowSelectFlag"
              :headers="tableColumns1"
              :items="tableData1"
              :items-per-page="tableOptions1.ItemsPerPage"
              :footer-props="tableOptions1.FooterProps"
              hover
              hide-default-footer
            >
              <!-- Status Chip Template -->
              <template v-slot:item.ActiveStatusTxt="{ item }">
                <v-chip
                  :color="getActiveStatusColor(item.ActiveStatusTxt)"
                  dark
                  class="status-chip"
                >
                  <v-icon small left>mdi-circle-medium</v-icon>
                  {{ item.ActiveStatusTxt }}
                </v-chip>
              </template>

              <!-- Image Template -->
              <template v-slot:item.MemberImagePath="{ item }">
                <v-avatar size="60" class="my-2">
                  <img :src="item.MemberImagePath" />
                </v-avatar>
              </template>

              <!-- Actions Template -->
              <template v-slot:item.Actions="{ item }">
                <div class="action-buttons-group">
                  <v-btn
                    v-if="item.PreviewFlag"
                    icon
                    small
                    color="info"
                    @click="previewAlert(item, $event)"
                    class="action-icon-btn"
                    v-tooltip.top="'Preview'"
                  >
                    <v-icon size="18">mdi-file-search</v-icon>
                  </v-btn>

                  <v-btn
                    v-if="item.EditButtonFlag"
                    icon
                    small
                    color="warning"
                    @click="editAlert(item, $event)"
                    class="action-icon-btn"
                    v-tooltip.top="'Edit'"
                  >
                    <v-icon size="18">mdi-pencil</v-icon>
                  </v-btn>

                  <v-btn
                    v-if="item.DeleteButtonFlag"
                    icon
                    small
                    color="error"
                    @click="deleteAlert(item, $event)"
                    class="action-icon-btn"
                    v-tooltip.top="'Delete'"
                  >
                    <v-icon size="18">mdi-trash-can</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-data-table>

            <!-- Custom Footer -->
            <v-divider></v-divider>
            <v-card-text class="table-footer pa-4">
              <div class="d-flex align-center justify-space-between">
                <span class="text-caption text-grey-1">
                  Showing <strong>{{ tableData1.length }}</strong> question(s)
                </span>
              </div>
            </v-card-text>
          </v-card>
        </v-card-text>
      </v-sheet>

      <!-- Footer Actions -->
      <v-divider></v-divider>
      <v-card-actions class="list-footer pa-4">
        <v-spacer />
        <v-btn
          color="primary"
          elevation="4"
          rounded
          height="40"
          min-width="120"
          @click="closeQuestionsPopup"
          class="close-list-btn"
        >
          <v-icon small left>mdi-close</v-icon>
          CLOSE
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Create Dialog -->
    <v-dialog v-model="addRecordPrompt" persistent max-width="70%">
      <judging-criteria-questions-create
        :addRecordPrompt="addRecordPrompt"
        :JudgingCriteriaId="JudgingCriteria"
        @hideAddRecordPrompt="hideAddRecordPrompt($event)"
        v-if="addRecordPrompt"
      ></judging-criteria-questions-create>
    </v-dialog>

    <!-- Edit Dialog -->
    <v-dialog v-model="editRecordPrompt" persistent max-width="75%">
      <judging-criteria-questions-edit
        :editRecordPrompt="editRecordPrompt"
        @hideEditRecordPrompt="hideEditRecordPrompt($event)"
        :CriteriaQuestionsId="recordData.CriteriaQuestionsId"
        v-if="editRecordPrompt"
      ></judging-criteria-questions-edit>
    </v-dialog>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import JudgingCriteriaQuestionsCreate from "@/view/pages/erp/jci-award/judging-criteria-questions/JudgingCriteriaQuestionsCreate.vue";
import JudgingCriteriaQuestionsEdit from "@/view/pages/erp/jci-award/judging-criteria-questions/JudgingCriteriaQuestionsEdit.vue";
import Swal from "sweetalert2";

export default {
  mixins: [common],
  components: {
    JudgingCriteriaQuestionsCreate,
    JudgingCriteriaQuestionsEdit,
  },
  props: {
    CriteriaId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      valid1: true,
      search: "",
      ProgessStart: 0,
      uploadPercentage: 0,
      OverlayLoadingFlag: false,

      SelectFlag: true,
      DeleteFlag: false,
      SubmitFlag: false,

      alert: {},

      valid: true,

      RoleId: "",

      ResultFlag: 0,

      JudgingCriteriaRules: [(v) => !!v || "Judging Criteria is required"],
      JudgingCriteria: "",
      JudgingCriteriaOptions: [],

      addRecordPrompt: false,
      editRecordPrompt: false,
      previewRecordPrompt: false,

      recordData: {},

      dialog: false,
      dialogDelete: false,
      rows: [],
      tableData1: [],
      tableOptions1: {
        CreateButtonFlag: true,
        ItemKey: "CriteriaQuestionsId",
        SingleSelectFlag: false,
        ShowSelectFlag: false,
        ItemsPerPage: 10,
        FooterProps: {},
      },
      tableColumns1: [],
      TotalMembers: 0,

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      CurrentYearId: 0,
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
      FloatingButtonOptions: {},
    };
  },
  computed: {},
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        var flag = tr.PagePermissionFlag;
        console.log("PagePermissionFlag=" + flag);

        if (!flag) {
          this.$router.push("/access-denied");
        }

        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;
        this.FloatingButtonOptions = tr.FloatingButtonOptions;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        this.CurrentYearId = CurrentJciYearId;

        // this.getJudgingCriteriaOptions();
        this.searchForm();
      }
    },
    JudgingCriteria: function () {
      this.rows = [];
      this.tableData1 = [];
      this.tableColumns1 = [];
      // Don't reset tableOptions1 as it breaks CreateButtonFlag
      // tableOptions1 will be updated by API response
    },
    JudgingCriteriaOptions: function () {
      console.log("watch JudgingCriteriaOptions");
      this.JudgingCriteriaOptionsLoading = false;
    },
    CriteriaId: function () {
      console.log("watch CriteriaId");
      if (this.CriteriaId) {
        this.JudgingCriteria = this.CriteriaId;
        console.log({ JudgingCriteria: this.JudgingCriteria });
      }
    },
  },
  created() {
    // Initialize JudgingCriteria from the prop immediately
    if (this.CriteriaId) {
      this.JudgingCriteria = this.CriteriaId;
      console.log("created: JudgingCriteria set to", this.JudgingCriteria);
    }
  },
  methods: {
    closeQuestionsPopup() {
      console.log("closeQuestionsPopup called");
      this.$emit("hideQuestionsRecordPrompt", false);
    },
    hideAddRecordPrompt(flag) {
      console.log("hideAddRecordPrompt called");
      this.addRecordPrompt = false;
      console.log({ flag });
      if (flag) {
        this.searchForm();
      }
    },
    hidePreviewRecordPrompt() {
      console.log("hidePreviewRecordPrompt called");
      this.previewRecordPrompt = false;
    },
    previewAlert(tr) {
      console.log("previewAlert called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.recordData = tr;
        this.previewRecordPrompt = true;
      } else {
        var message = "Kindly select one record to preview";
        this.errorMessage(message);
      }
    },
    hideEditRecordPrompt(flag) {
      console.log("hideEditRecordPrompt called");
      this.editRecordPrompt = false;
      console.log({ flag });
      if (flag) {
        this.searchForm();
      }
    },
    editAlert(tr) {
      console.log("editAlert called");
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.recordData = tr;
        this.editRecordPrompt = true;
      } else {
        var message = "Kindly select one record to edit";
        this.errorMessage(message);
      }
    },
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "judging_criteria_question",
        Action: "list",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    getJudgingCriteriaOptions() {
      console.log("getJudgingCriteriaOptions called");
      var selectbox1_source = "JudgingCriteria";
      var selectbox1_destination = "JudgingCriteriaOptions";
      var selectbox1_url = "api/judging-criteria/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    searchForm() {
      console.log("searchForm called");

      var validate1 = true;
      console.log("validate1=" + validate1);

      if (validate1) {
        this.tableData1 = [];
        this.selected1 = [];
        this.SubmitFlag = true;
        this.OverlayLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/judging-criteria-questions/lists";
        var upload = {
          UserInterface: 1,
          JudgingCriteria: this.JudgingCriteria,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = [];
        var table_content = [];
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        // thisIns.ProgessStart = 1;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.ProgessStart = 0;
            thisIns.SubmitFlag = false;
            thisIns.OverlayLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);

            var options = records.TableOptions;

            thisIns.tableOptions1 = options;

            if (flag == 1) {
              thisIns.tableColumns1 = records.TableHeader;
              thisIns.tableData1 = records.TableData;

              thisIns.toast("success", output);
            } else {
              console.log("error=" + output);
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.OverlayLoadingFlag = false;
          });
      } else {
        var message = "Kindy fill required fields and then submit";
        this.toast("error", message);
      }
    },
    deleteAlert(tr) {
      console.log("deleteAlert called");
      console.log("tr=" + JSON.stringify(tr));
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.selectedData = tr;
        this.confirmAlert();
      } else {
        var message = "Kindly select one record to delete";
        this.sweetAlert("error", message, false);
      }
    },
    confirmAlert() {
      console.log("confirmAlert called");
      var tr = this.selectedData;
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        var htmlTxt = "";
        htmlTxt += "<br/>Criteria Detail <b> " + tr.CriteriaDetail + "</b>";
        htmlTxt += "<br/> Questions <b> " + tr.CriteriaQuestionsText + "</b>";
        htmlTxt +=
          "<br/>Questions Chars Count <b> " +
          tr.CriteriaQuestionsCharsCount +
          "</b>";
        htmlTxt += "<br/><br/>Do you want to delete the data?";

        Swal.fire({
          title: "Are you sure?",
          text: "Do you want to delete the data?",
          icon: "warning",
          html: htmlTxt,
          showCancelButton: true,
          confirmButtonText: `Delete`,
          confirmButtonColor: "#d33",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#3085d6",
        }).then((result) => {
          if (result.isConfirmed) {
            this.deleteRecord(tr);
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        var message = "Kindly select one record to delete";
        this.sweetAlert("error", message, false);
      }
    },
    deleteRecord(tr) {
      console.log("deleteRecord called");

      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      console.log({ tr });

      if (n1 > 0) {
        this.OverlayLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/judging-criteria-questions/destroy";
        var upload = {
          UserInterface: 1,
          CriteriaQuestions: tr.CriteriaQuestionsId,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.OverlayLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            if (flag == 1) {
              thisIns.sweetAlert("success", output, false);
              thisIns.searchForm();
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.OverlayLoadingFlag = false;
          });
      } else {
        console.log("some errors occured");
        var message = "Kindly select one member to delete";
        this.sweetAlert("error", message, false);
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>

<style lang="scss" scoped>
#judging-criteria-questions-list {
  width: 100%;
  height: 100%;
}

/* ============================================
   LIST CARD
   ============================================ */
.list-card {
  border-radius: 16px !important;
  overflow: hidden;
  animation: listSlideIn 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

/* ============================================
   LIST HEADER
   ============================================ */
.list-header {
  background: linear-gradient(135deg, #8950fc 0%, #24bde4 100%);
  padding: 20px 24px;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 70%
    );
    animation: rotate 20s linear infinite;
  }

  .header-left {
    display: flex;
    align-items: center;
    z-index: 1;
    animation: slideInLeft 0.6s ease-out;
    gap: 16px;
  }

  .header-icon-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.2);
    flex-shrink: 0;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    animation: pulse 2s ease-in-out infinite;
  }

  .header-text-section {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .header-right {
    display: flex;
    gap: 8px;
    z-index: 1;
  }

  .header-title {
    font-size: 1.35rem;
    font-weight: 800;
    margin: 0;
    letter-spacing: 0.5px;
    line-height: 1.2;
    color: white;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .header-subtitle {
    font-size: 0.8rem;
    opacity: 0.92;
    margin: 6px 0 0 0;
    font-weight: 500;
    letter-spacing: 0.2px;
    color: rgba(255, 255, 255, 0.95);
  }
}

.header-action-btn {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;

  &:hover {
    transform: scale(1.15) !important;
    background: rgba(255, 255, 255, 0.2) !important;
  }

  &:active {
    transform: scale(0.95) !important;
  }
}

/* ============================================
   LIST CONTENT
   ============================================ */
.list-content-sheet {
  background: linear-gradient(to bottom, #f5f7fa 0%, #ffffff 100%) !important;
  padding: 0 !important;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(137, 80, 252, 0.05);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(137, 80, 252, 0.3);
    border-radius: 10px;

    &:hover {
      background: rgba(137, 80, 252, 0.5);
    }
  }
}

.list-body-content {
  padding: 24px !important;
  animation: fadeIn 0.6s ease-out 0.2s both;
}

/* ============================================
   ADD BUTTON SECTION
   ============================================ */
.add-button-section {
  display: flex;
  justify-content: flex-start;

  .add-question-btn {
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 16px rgba(137, 80, 252, 0.25) !important;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(137, 80, 252, 0.4) !important;
    }

    &:active {
      transform: translateY(0);
    }
  }
}

/* ============================================
   EMPTY STATE
   ============================================ */
.empty-state-card {
  border-radius: 12px !important;
  border: 2px dashed rgba(137, 80, 252, 0.2) !important;
  background: linear-gradient(
    135deg,
    rgba(137, 80, 252, 0.02) 0%,
    rgba(29, 185, 84, 0.02) 100%
  ) !important;
  animation: fadeIn 0.6s ease-out;

  .v-card__text {
    padding: 40px 20px !important;

    .v-icon {
      animation: float 4s ease-in-out infinite;
    }

    h3 {
      color: #8950fc;
      font-weight: 700;
      margin-bottom: 8px;
    }

    p {
      color: #999;
      font-size: 14px;
    }
  }
}

/* ============================================
   TABLE WRAPPER
   ============================================ */
.table-wrapper-card {
  border-radius: 12px !important;
  overflow: hidden;
  animation: slideInUp 0.6s ease-out;

  ::v-deep .v-data-table {
    background: transparent !important;

    &__wrapper {
      table {
        border-collapse: collapse;

        thead {
          tr {
            background: linear-gradient(90deg, #8950fc 0%, #698af7 100%);

            th {
              background: transparent !important;
              color: white !important;
              font-weight: 700 !important;
              font-size: 13px !important;
              padding: 16px 12px !important;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              border: none !important;
            }
          }
        }

        tbody {
          tr {
            border-bottom: 1px solid rgba(137, 80, 252, 0.1) !important;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

            &:hover {
              background: linear-gradient(
                90deg,
                rgba(137, 80, 252, 0.05) 0%,
                rgba(29, 185, 84, 0.05) 100%
              ) !important;
              transform: scale(1.005);
              box-shadow: inset 0 2px 8px rgba(137, 80, 252, 0.05);
            }

            td {
              padding: 14px 12px !important;
              color: #333;
              font-size: 13px;
            }

            &:last-child {
              border-bottom: none !important;
            }
          }
        }
      }
    }
  }
}

.modern-questions-table {
  ::v-deep {
    .v-data-table__wrapper {
      border-radius: 8px;
    }
  }
}

/* ============================================
   SEARCH FIELD
   ============================================ */
.bg-light {
  background: linear-gradient(90deg, #f9fafb 0%, #ffffff 100%);
  border-bottom: 1px solid rgba(137, 80, 252, 0.1);

  .search-field {
    ::v-deep .v-input__prepend-inner {
      margin-right: 12px;
    }

    ::v-deep .v-input__control {
      input::placeholder {
        color: #bdc3c7 !important;
        opacity: 1;
      }
    }
  }
}

/* ============================================
   TABLE FOOTER
   ============================================ */
.table-footer {
  background: linear-gradient(90deg, #f9fafb 0%, #ffffff 100%);
  border-top: 1px solid rgba(137, 80, 252, 0.1);
  padding: 12px 24px !important;
}

/* ============================================
   STATUS CHIP
   ============================================ */
.status-chip {
  font-weight: 700 !important;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  font-size: 11px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25) !important;
  }
}

/* ============================================
   ACTION BUTTONS
   ============================================ */
.action-buttons-group {
  display: flex;
  gap: 6px;
  justify-content: center;
}

.action-icon-btn {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 6px !important;

  &:hover {
    transform: scale(1.25) rotate(5deg);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: scale(0.9);
  }
}

/* ============================================
   LIST FOOTER
   ============================================ */
.list-footer {
  background: linear-gradient(90deg, #f9fafb 0%, #ffffff 100%);
  border-top: 1px solid rgba(137, 80, 252, 0.1);
  padding: 16px 24px !important;

  .close-list-btn {
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 12px rgba(137, 80, 252, 0.2) !important;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(137, 80, 252, 0.3) !important;
    }

    &:active {
      transform: translateY(0);
    }
  }
}

/* ============================================
   ANIMATIONS
   ============================================ */
@keyframes listSlideIn {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(20px);
  }

  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-10px);
  }
}

@keyframes pulse {
  0%,
  100% {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  50% {
    box-shadow: 0 4px 20px rgba(255, 255, 255, 0.3);
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

/* ============================================
   RESPONSIVE DESIGN
   ============================================ */
@media (max-width: 960px) {
  .list-header {
    flex-direction: column;
    text-align: center;
    align-items: center !important;

    .header-left {
      justify-content: center;
      margin-bottom: 12px;
    }

    .header-right {
      width: 100%;
      justify-content: center;
    }

    .header-title {
      font-size: 1.1rem;
    }

    .header-subtitle {
      font-size: 0.8rem;
    }
  }

  .list-body-content {
    padding: 16px !important;
  }

  .add-button-section {
    .add-question-btn {
      width: 100%;
    }
  }
}

@media (max-width: 600px) {
  .list-card {
    max-height: 95vh;
    max-width: 95vw;
  }

  .list-header {
    padding: 16px !important;

    .header-left {
      flex-direction: column;
      text-align: center;
    }

    .header-icon-badge {
      margin-bottom: 8px;
    }

    .header-title {
      font-size: 1rem;
    }

    .header-subtitle {
      display: none;
    }

    .header-right {
      margin-top: 8px;
    }
  }

  .list-body-content {
    padding: 12px !important;
  }

  .action-buttons-group {
    gap: 4px;
  }

  .action-icon-btn {
    ::v-deep .v-icon {
      font-size: 16px !important;
    }
  }

  .table-footer {
    padding: 8px 12px !important;
  }
}

/* ============================================
   ADDITIONAL STYLING
   ============================================ */
::v-deep .v-tooltip__content {
  background: rgba(0, 0, 0, 0.9) !important;
  border-radius: 6px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

::v-deep .v-card {
  box-shadow: 0 4px 20px rgba(137, 80, 252, 0.1) !important;
}

/* Custom scrollbar for the entire list */
#judging-criteria-questions-list {
  ::v-deep ::-webkit-scrollbar {
    width: 8px;
  }

  ::v-deep ::-webkit-scrollbar-track {
    background: rgba(137, 80, 252, 0.05);
    border-radius: 10px;
  }

  ::v-deep ::-webkit-scrollbar-thumb {
    background: rgba(137, 80, 252, 0.3);
    border-radius: 10px;

    &:hover {
      background: rgba(137, 80, 252, 0.5);
    }
  }
}
</style>
